/**
 * TRAC-1052: a tester can turn the outage screen on for THEIR OWN BROWSER.
 *
 * The dev outage screen used to be one switch for the whole environment: the
 * announcement row that `GET /api/settings/announcement` serves. Flipping it
 * for one sweep test put the screen in front of every tester on dev.
 *
 * The overlay is driven entirely by the embed's announcement poll, and no
 * backend chat or handoff path reads the announcement row, so forcing the
 * poll's RESULT in one browser reproduces the real screen faithfully. The QA
 * dashboard gear writes this localStorage key on the frontend origin; the
 * embed reads it on every poll and rewrites the response to what an outage
 * looks like. Everything downstream (hours, the handoff button, TRAC-1006
 * dismissal, fallback) runs the same code as a real outage, untouched.
 *
 * Why localStorage reaches the embed: the QA page (dev.traci.mi.tirerack.com
 * as a top-level page) and the /embed iframe inside www.tirerack.com are the
 * same origin AND the same site (tirerack.com), so Chrome's third-party
 * storage partitioning puts them in one partition. Verified with Chromium 139
 * with partitioning on: a cross-site host page reads null, www.tirerack.com
 * reads the key.
 *
 * Never in production: the read is hard off when VITE_ENV is "prod", so a
 * leftover key in someone's browser can never put the screen on prod.
 */
import { AGENT_UNAVAILABLE_MESSAGE } from "../../constants/humanContact";

export const OUTAGE_PREVIEW_KEY = "traci_qa_outage_preview";

/** Shown on the overlay itself so nobody mistakes the preview for a real outage. */
export const OUTAGE_PREVIEW_NOTE =
  "Only you see this outage screen — turn it off on the QA page";

const currentEnv = (): string => import.meta.env.VITE_ENV || "dev";

/** The preview exists on every build except production. */
export function outagePreviewAllowed(env: string = currentEnv()): boolean {
  return env !== "prod";
}

/** True when this browser asked for the outage screen and the build allows it. */
export function outagePreviewOn(env: string = currentEnv()): boolean {
  if (!outagePreviewAllowed(env)) return false;
  try {
    return window.localStorage.getItem(OUTAGE_PREVIEW_KEY) === "1";
  } catch {
    // Storage blocked (privacy mode, sandboxed frame): no preview, never an error.
    return false;
  }
}

/** Written by the QA settings toggle. Returns whether the write took. */
export function setOutagePreview(on: boolean): boolean {
  try {
    if (on) window.localStorage.setItem(OUTAGE_PREVIEW_KEY, "1");
    else window.localStorage.removeItem(OUTAGE_PREVIEW_KEY);
    return true;
  } catch {
    return false;
  }
}

interface AnnouncementLike {
  enabled?: boolean;
  message?: string | null;
  mode?: "informational" | "emergency";
}

/**
 * The announcement poll's response as this browser should see it. With the
 * preview off it is returned untouched. With it on, it becomes an emergency
 * announcement carrying the server's message when the server is actually
 * announcing something, otherwise the default copy the real auto-maintenance
 * outage shows (the backend's AUTO_MAINTENANCE_MESSAGE is the same constant).
 *
 * A DISABLED row's message is ignored on purpose: the row keeps whatever text
 * was last typed into it (on dev, 18 Sep: "QA test — TRAC-525"), which is not
 * what a customer would see in an outage.
 */
export function applyOutagePreview<T extends AnnouncementLike>(
  data: T,
  on: boolean = outagePreviewOn(),
): T {
  if (!on) return data;
  const serverMessage =
    data.enabled && typeof data.message === "string" ? data.message.trim() : "";
  return {
    ...data,
    enabled: true,
    mode: "emergency",
    message: serverMessage ? data.message : AGENT_UNAVAILABLE_MESSAGE,
  };
}
